import type { Menu } from './types';
import Language from 'language';

import { MyCareRoutes } from 'pages/my-care/my-care.constants';
import { CarePlanRoutes } from 'pages/care-plan/care-plan.constants';
import { ModalTypesMap } from 'react-components/handshake/state/constants';
import { getStore } from 'app/hermes-redux';
import { openZendeskLinkWithToken } from 'state/global/actions';

import { LEAGUE_MODULE_NAMES } from '@leagueplatform/core';

export const MENU_STATES_MAPPING = {
  // work
  home: ['home'],
  feed: ['feed', 'feedSearch', 'feedDetail'],
  directory: ['directory', 'directorySearch'],
  groups: ['groups'],
  leaderboard: ['leaderboard'],
  profile: ['profile', 'profileEdit'],
  settings: ['settings'],

  // perks
  perksHomePage: [
    'perksHomePage',

    'shop',
    'shopCategory',
    'shopSearch',
    'shopDetailsCashback',
    'shopDetailsOffer',
    'cashbackSearch',

    'shopOnline',
    'shopOnlineBase',
    'shopOnlineSearch',
    'shopOnlineDetailsCashback',
    'shopOnlineDetailsOffer',

    'instoreOffers',
    'instoreOffersDetails',
    'top10OffersSearch',

    'colleagueOffers',
    'colleagueOffersDetails',
    'exclusiveOffersSearch',

    'restaurants',
    'restaurantsDetails',
    'restaurantsError',

    'giftcards',
    'giftcardsDetails',
    'giftcardsPopular',
    'giftcardsCategory',
    'giftcardsPayment',
    'giftcardsCodes',
    'giftcardsConfirmation',
    'giftcardsError',
    'giftCardsSearch',

    'cinemas',
    'cinemasDetails',
    'cinemasCodes',
    'cinemasPayment',
    'cinemasConfirmation',
  ],
  wallet: ['wallet'],
  spotRewards: [
    'spotRewards',
    'spotRewardsDetails',
    'spotRewardsPopular',
    'spotRewardsCategory',
    'spotRewardsPayment',
    'spotRewardsCodes',
    'spotRewardsConfirmation',
    'spotRewardsError',
    'spotRewardsSearch',
  ],

  // life
  employeeAssistance: [
    'employeeAssistance',
    'employeeAssistanceHome',
    'employeeAssistanceCategoriesListing',
    'employeeAssistanceCategories',
    'employeeAssistanceSearch',
    'employeeAssistanceArticles',
    'employeeAssistanceError',
  ],
  hwrHome: [
    'hwrHome',
    'employeeAssistance',
    'employeeAssistanceCategoriesListing',
    'employeeAssistanceCategories',
    'employeeAssistanceSearch',
    'employeeAssistanceArticles',
    'employeeAssistanceError',
  ],
  healthLibrary: ['healthLibrary'],
  wellnessTools: ['wellnessTools'],
  legalServices: ['legalServices'],
  chat: ['chat'],
  needHelp: ['needHelp'],
  generalEnquiry: ['generalEnquiry'],
  childCare: ['childCare'],
  counsellorLocator: ['counsellorLocator'],
  wellBeingHub: [
    'wellBeingHub',

    'tiering',
    'tierRewards',

    'challengesHub',
    'challengeActivity',
    'challengeDetails',
    'challengesJoinHabit',
    'challengesHabitActivity',
  ],
  myCare: [
    MyCareRoutes.MY_CARE_HUB,
    MyCareRoutes.CARE_PATH_HOME,
    CarePlanRoutes.OVERVIEW,
    CarePlanRoutes.ACTIVITY,

    'programsAll',
    'programDetail',
    'moduleDetail',
    'programDetailOverview',

    'selfAssessmentHub',
    'selfAssessmentAssessment',
    'selfAssessmentResults',
    'selfAssessmentHistoricalResult',

    LEAGUE_MODULE_NAMES.healthJourney,
    LEAGUE_MODULE_NAMES.healthProgress,
    LEAGUE_MODULE_NAMES.healthPrograms,
    LEAGUE_MODULE_NAMES.healthJourneyHistory,
    `${LEAGUE_MODULE_NAMES.program}-history`,
    LEAGUE_MODULE_NAMES.program,
    `${LEAGUE_MODULE_NAMES.healthJourney}-activities-list`,
    LEAGUE_MODULE_NAMES.careLibrary,
  ],
};

export const KeyboardShortcutsMenuId = 'keyboard-shortcuts';
export const THVCMenuId = ModalTypesMap.VirtualCare;
export const THWBMenuId = ModalTypesMap.Wellbeing;

export const getInitialModel = (): Menu => ({
  home: {
    title: 'components.main_menu.home',
    order: 0,
    id: 'home',
    sections: [],
  },
  myCare: {
    title: 'components.main_menu.care_path',
    order: 1,
    id: 'myCare',
    sections: [],
    hasSubMenu: true,
  },
  life: {
    title: 'components.main_menu.library',
    order: 2,
    id: 'life',
    sections: [],
  },
  wellBeing: {
    title: 'components.main_menu.well_being_hub',
    order: 3,
    id: 'wellBeing',
    sections: [],
  },
  feed: {
    title: 'components.main_menu.workplace',
    order: 4,
    id: 'feed',
    sections: [],
  },
  deals: {
    title: 'components.main_menu.deals',
    order: 5,
    id: 'deals',
    sections: [],
    hasSubMenu: true,
  },
  company: {
    title: 'components.main_menu.company.title',
    order: 6,
    id: 'company',
    sections: [],
  },
  myAccount: {
    title: 'components.main_menu.myAccount',
    order: 7,
    id: 'myAccount',
    sections: [],
  },
});

export const getStaticSections = () => {
  const getLanguageLabel = item =>
    `${polyglot.t(`components.main_menu.language`)} ${polyglot.t(
      `supported_languages.${item}.name`,
    )} (${polyglot.t(`supported_languages.${item}.country_code`)})`;

  const selectedLanguage = getLanguageLabel(Language.getLocale());

  return {
    language: {
      data: { title: selectedLanguage },
      icon: false,
      name: 'settings',
      id: 'language-settings',
      group: 0,
    },
    logout: {
      data: { title: 'components.main_menu.logout' },
      type: 'link',
      icon: false,
      state: 'logout',
      id: 'logout',
      group: 0,
    },
    keyboardShortcuts: {
      type: 'menuButtonContextHandled',
      name: 'keyboard-shortcuts',
      data: {
        title: 'components.main_menu.keyboard_shortcuts',
      },
      id: KeyboardShortcutsMenuId,
      icon: false,
      group: 0,
    },
    accessibilityTips: {
      type: 'dynamicLink',
      name: 'accessibility-tips',
      data: {
        title: 'components.main_menu.accessibility_tips',
      },
      id: 'accessibility-tips',
      icon: true,
      group: 0,
      onClick: () => {
        getStore().dispatch(openZendeskLinkWithToken('accessibility_tips'));
      },
    },
  };
};
